/**
 * Responsive image sources.
 * ---------------------------------------------------------------------------
 * Every product photograph on this site is served from one place:
 *
 *     https://riymay.com/assets/<filename>
 *
 * The uploader now also writes down-scaled copies of new images, and records
 * the widths it actually produced on the image record:
 *
 *     { url: "1744223023256-IMG_0198.jpeg", altText: "...", widths: [400, 800, 1600] }
 *
 * A variant file is the original's name with the width inserted before the
 * extension:
 *
 *     1744223023256-IMG_0198.jpeg  ->  1744223023256-IMG_0198-800w.jpeg
 *
 * ONLY the widths listed in `widths` exist on disk. Any other width is a 404,
 * and a 404 inside a srcSet is a broken image — so this module never invents a
 * width, never guesses, and never derives one from the original's dimensions.
 * It reads `widths` or it returns null.
 *
 * `widths` is absent on every image uploaded before the variant pipeline
 * existed, which today is almost all of them. That is the normal case, not an
 * error case: `buildSrcSet` returns null, the caller omits `srcSet` and
 * `sizes`, and the markup is byte-for-byte what it was before. Callers keep
 * their existing plain `src` pointing at the ORIGINAL filename in every case,
 * which is also the fallback for browsers without srcset support.
 *
 * Nothing here throws. An image that is undefined, a url that is missing, a
 * `widths` that is a string, a number, an empty array or an array of junk all
 * come back as null.
 */

const ASSET_BASE = "https://riymay.com/assets";

/** Full URL for an asset filename. Returns null for anything unusable. */
export function assetUrl(filename) {
  if (typeof filename !== "string") return null;
  const name = filename.trim();
  if (!name) return null;
  return `${ASSET_BASE}/${name}`;
}

/**
 * URL of the `width`-wide variant of `filename`.
 *
 * "photo.jpeg" + 800 -> https://riymay.com/assets/photo-800w.jpeg
 * A name with no extension gets the suffix appended: "photo" -> "photo-800w".
 * Only call this with a width that came out of the image's own `widths`.
 */
export function variantUrl(filename, width) {
  if (typeof filename !== "string") return null;
  const name = filename.trim();
  if (!name) return null;

  const w = Number(width);
  if (!Number.isFinite(w) || w <= 0) return null;
  const suffix = `-${Math.round(w)}w`;

  // Last dot only, and only if it is a real extension: a leading dot is a
  // dotfile, not an extension, and a trailing dot has nothing after it.
  const dot = name.lastIndexOf(".");
  const base = dot > 0 && dot < name.length - 1 ? name.slice(0, dot) : name;
  const ext = dot > 0 && dot < name.length - 1 ? name.slice(dot) : "";

  return `${ASSET_BASE}/${base}${suffix}${ext}`;
}

/**
 * srcSet string built ONLY from the widths the image record declares, or null
 * when there are none to build from.
 *
 * Accepts the image record itself ({ url, widths }). Duplicate and malformed
 * widths are dropped and the rest are sorted ascending, so a candidate list is
 * either correct and complete or absent altogether.
 */
export function buildSrcSet(image) {
  if (!image || typeof image !== "object") return null;

  const filename = typeof image.url === "string" ? image.url.trim() : "";
  if (!filename) return null;

  if (!Array.isArray(image.widths) || image.widths.length === 0) return null;

  const seen = new Set();
  const widths = [];
  image.widths.forEach((raw) => {
    const w = Math.round(Number(raw));
    if (!Number.isFinite(w) || w <= 0 || seen.has(w)) return;
    seen.add(w);
    widths.push(w);
  });

  if (widths.length === 0) return null;
  widths.sort((a, b) => a - b);

  const entries = widths
    .map((w) => {
      const url = variantUrl(filename, w);
      return url ? `${url} ${w}w` : null;
    })
    .filter(Boolean);

  return entries.length > 0 ? entries.join(", ") : null;
}

/**
 * Convenience for the many call sites that spread onto an <img>: gives back
 * `{ srcSet, sizes }` when there are variants, and `{}` when there are not —
 * so spreading it adds nothing at all in the no-variants case.
 *
 *   <img src={...} {...responsiveProps(image, SIZES)} />
 */
export function responsiveProps(image, sizes) {
  const srcSet = buildSrcSet(image);
  if (!srcSet) return {};
  return sizes ? { srcSet, sizes } : { srcSet };
}

export default { assetUrl, variantUrl, buildSrcSet, responsiveProps };
